import { useState, useEffect } from 'react';

interface RestorePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  backupJobId: string;
  backupConfigName: string;
  restoreLocation: 'original' | 'custom';
  customPath?: string;
  onConfirmRestore: () => void;
}

interface RestorePreviewFile {
  path: string;
  size: number;
  size_human: string;
  mode?: string;
  will_overwrite: boolean;
  is_database: boolean;
}

interface RestorePreviewData {
  backup_id: string;
  backup_name: string;
  backup_type: string;
  total_files: number;
  total_size: number;
  total_size_human: string;
  files: RestorePreviewFile[];
  will_overwrite: boolean;
  overwrite_count: number;
  new_files_count: number;
}

export const RestorePreviewModal = ({
  isOpen,
  onClose,
  backupJobId,
  backupConfigName,
  restoreLocation,
  customPath,
  onConfirmRestore,
}: RestorePreviewModalProps) => {
  const [preview, setPreview] = useState<RestorePreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchPreview();
    }
  }, [isOpen, backupJobId, restoreLocation, customPath]);

  const fetchPreview = async () => {
    setLoading(true);
    setError('');
    setPreview(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/restore/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          backup_job_id: backupJobId,
          restore_location: restoreLocation,
          custom_path: customPath || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load restore preview');
      }

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load restore preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
      <div className="bg-surface-card-light dark:bg-surface-card-dark rounded-2xl shadow-elevated max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col border border-surface-border-light dark:border-surface-border-dark overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-surface-border-light dark:border-surface-border-dark flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-widest">
                Restore Preview
              </h2>
              <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1 font-medium">
                Target Backup:{' '}
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {backupConfigName}
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              <span className="text-xs font-black uppercase tracking-[0.2em] text-text-subtle-light dark:text-text-subtle-dark">
                Compiling Manifest...
              </span>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl p-4">
              <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                {error}
              </p>
            </div>
          )}

          {/* Preview data */}
          {preview && !loading && (
            <div className="space-y-8">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SummaryCard
                  label="Target Items"
                  value={preview.total_files.toString()}
                  color="brand"
                />
                <SummaryCard
                  label="Telemetry Weight"
                  value={preview.total_size_human}
                  color="emerald"
                />
                <SummaryCard
                  label="Infrastructure Type"
                  value={preview.backup_type}
                  color="purple"
                />
              </div>

              {/* Overwrite Warning */}
              {preview.will_overwrite && (
                <div className="bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-state-warn-light dark:text-state-warn-dark"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-xs font-black uppercase tracking-widest text-state-warn-light dark:text-state-warn-dark">
                        Resource Conflict Detected
                      </h3>
                      <div className="mt-1 text-sm text-state-warn-light dark:text-state-warn-dark font-medium">
                        <p>
                          {preview.overwrite_count} established infrastructure assets will be
                          replaced during this operation.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* File List */}
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-text-subtle-light dark:text-text-subtle-dark mb-4 ml-1">
                  Proposed Infrastructure Changes
                </h3>

                {preview.files.length === 0 ? (
                  <div className="text-center py-12 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border-2 border-dashed border-surface-border-light dark:border-surface-border-dark">
                    <p className="text-sm font-bold text-text-subtle-light dark:text-text-subtle-dark">
                      Empty manifestation. No assets detected.
                    </p>
                  </div>
                ) : (
                  <div className="bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-2xl overflow-hidden shadow-soft">
                    <div className="bg-surface-2-light dark:bg-surface-2-dark px-4 py-3 border-b border-surface-border-light dark:border-surface-border-dark grid grid-cols-12 gap-4 text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                      <div className="col-span-1 text-center">Type</div>
                      <div className="col-span-7">Target Path</div>
                      <div className="col-span-2 text-right">Telemetry</div>
                      <div className="col-span-2 text-center">Operation</div>
                    </div>
                    <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark max-h-96 overflow-y-auto custom-scrollbar">
                      {preview.files.map((file, index) => (
                        <div
                          key={index}
                          className="px-4 py-3 grid grid-cols-12 gap-4 items-center transition-colors hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                        >
                          <div className="col-span-1 flex justify-center">
                            {file.is_database ? (
                              <div className="p-1.5 bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark rounded-lg">
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2.5}
                                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <div className="p-1.5 bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400 rounded-lg">
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2.5}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="col-span-7 truncate text-slate-700 dark:text-slate-300 font-mono text-[11px] font-bold">
                            {file.path}
                          </div>
                          <div className="col-span-2 text-right text-xs font-black text-text-muted-light dark:text-text-muted-dark">
                            {file.size_human}
                          </div>
                          <div className="col-span-2 text-center">
                            {file.will_overwrite ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter bg-state-warn-bg-light dark:bg-state-warn-bg-dark text-state-warn-light dark:text-state-warn-dark border border-state-warn-light/20 dark:border-state-warn-dark/20">
                                Overwrite
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark border border-state-ok-light/20 dark:border-state-ok-dark/20">
                                Provision
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File count summary */}
                {preview.files.length > 0 && (
                  <div className="mt-4 px-2 flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
                    <div>
                      Manifest: {preview.files.length} of {preview.total_files} assets manifest
                    </div>
                    <div className="flex gap-4">
                      {preview.new_files_count > 0 && (
                        <span className="text-state-ok-light dark:text-state-ok-dark">
                          {preview.new_files_count} New
                        </span>
                      )}
                      {preview.overwrite_count > 0 && (
                        <span className="text-state-warn-light dark:text-state-warn-dark">
                          {preview.overwrite_count} Replacement
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Restore Location Info */}
              <div className="bg-surface-2-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl p-4">
                <div className="text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-2">
                  Manifest Destination
                </div>
                <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                  {restoreLocation === 'original' ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>{' '}
                      Source Coordinates
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M3 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z"
                        />
                      </svg>{' '}
                      Workspace:{' '}
                      <span className="font-mono text-brand-600 dark:text-brand-400">
                        {customPath}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark border-t border-surface-border-light dark:border-surface-border-dark flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2.5 text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            Cancel
          </button>
          {preview && !loading && (
            <button
              onClick={() => {
                onClose();
                onConfirmRestore();
              }}
              className="px-8 py-2.5 bg-brand-600 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-brand-200 dark:shadow-none hover:bg-brand-700 transition-all active:scale-95"
            >
              Authorize Manifest Restore
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'brand' | 'emerald' | 'purple';
}) {
  const colors = {
    brand:
      'bg-accent-soft-light dark:bg-accent-soft-dark border-accent-text-light/20 dark:border-accent-text-dark/20 text-brand-700 dark:text-brand-400',
    emerald:
      'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark',
    purple:
      'bg-surface-2-light dark:bg-surface-2-dark border-surface-border-light dark:border-surface-border-dark text-text-muted-light dark:text-text-muted-dark',
  }[color];

  return (
    <div className={`border rounded-xl p-4 transition-all ${colors}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black mt-1 uppercase tracking-tight">{value}</div>
    </div>
  );
}
